import React, { useState, useEffect } from 'react';
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Loader2, Calendar, CheckCircle, XCircle, Users, Clock, FileText,
  Plus, AlertTriangle, DollarSign, Shield, Save, Sparkles, ClipboardCheck
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════════════
// VISIT WORKFLOW
// ═══════════════════════════════════════════════════════
function VisitWorkflow() {
  const [visits, setVisits] = useState([]);
  const [missed, setMissed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [patients, setPatients] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ patient_id: '', discipline: 'RN', staff_name: '', scheduled_date: '', visit_type: 'routine' });
  const [scheduling, setScheduling] = useState(false);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/workflow/visits?status=all`).then(r => r.json()),
      fetch(`${API}/api/workflow/visits/missed`).then(r => r.json()),
      fetch(`${API}/api/chart/patients-list`).then(r => r.json()),
    ]).then(([v, m, p]) => {
      setVisits(v.visits || []);
      setMissed(m.missed_visits || []);
      setPatients(p.patients || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const schedule = async () => {
    if (!form.patient_id || !form.staff_name || !form.scheduled_date) return;
    setScheduling(true);
    try {
      const res = await fetch(`${API}/api/workflow/visit/schedule`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      const visit = await res.json();
      setVisits(prev => [visit, ...prev]);
      setShowForm(false);
      setForm({ patient_id: '', discipline: 'RN', staff_name: '', scheduled_date: '', visit_type: 'routine' });
    } catch {}
    setScheduling(false);
  };

  const completeVisit = async (visitId) => {
    try {
      await fetch(`${API}/api/workflow/visit/complete`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visit_id: visitId, completed_by: "Staff", documentation_complete: true, pain_assessed: true, meds_reconciled: true, care_plan_reviewed: true }),
      });
      setVisits(prev => prev.map(v => v.id === visitId ? { ...v, status: 'completed' } : v));
    } catch {}
  };

  const statusColors = { scheduled: 'bg-sky-50 text-sky-500', completed: 'bg-sage-50 text-sage-400', missed: 'bg-terra-50 text-terra-400' };

  return (
    <div data-testid="visit-workflow">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-sage-400" />
          <h2 className="text-lg font-heading font-semibold text-[#2C3D30]">Visit Schedule</h2>
          {missed.length > 0 && <Badge className="bg-terra-400 text-white rounded-full text-xs animate-pulse">{missed.length} Missed</Badge>}
        </div>
        <Button onClick={() => setShowForm(!showForm)} size="sm" className="rounded-full bg-sage-400 hover:bg-sage-500 text-xs" data-testid="visit-schedule-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Schedule Visit
        </Button>
      </div>

      {showForm && (
        <div className="mb-4 p-4 rounded-2xl bg-sage-50 border border-sage-100 space-y-2" data-testid="visit-form">
          <div className="grid grid-cols-2 gap-2">
            <select value={form.patient_id} onChange={e => setForm(p => ({ ...p, patient_id: e.target.value }))} className="rounded-xl border border-sand-200 px-3 py-2 text-sm bg-white" data-testid="visit-patient">
              <option value="">Select patient...</option>
              {patients.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <select value={form.discipline} onChange={e => setForm(p => ({ ...p, discipline: e.target.value }))} className="rounded-xl border border-sand-200 px-3 py-2 text-sm bg-white" data-testid="visit-discipline">
              {['RN', 'LPN', 'SW', 'Chaplain', 'Aide', 'PT', 'OT', 'ST'].map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <Input value={form.staff_name} onChange={e => setForm(p => ({ ...p, staff_name: e.target.value }))} placeholder="Staff name" className="rounded-xl" data-testid="visit-staff" />
            <Input type="date" value={form.scheduled_date} onChange={e => setForm(p => ({ ...p, scheduled_date: e.target.value }))} className="rounded-xl" data-testid="visit-date" />
          </div>
          <Button onClick={schedule} disabled={scheduling} className="rounded-full bg-sage-400 hover:bg-sage-500 text-xs" data-testid="visit-save-btn">
            {scheduling ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />} Schedule
          </Button>
        </div>
      )}

      {loading ? <Loader2 className="w-5 h-5 animate-spin text-sage-400 mx-auto" /> : (
        <div className="space-y-2" data-testid="visit-list">
          {visits.slice(0, 20).map(v => (
            <div key={v.id} className="p-3 rounded-xl bg-sand-50 border border-sand-200 text-xs flex items-center justify-between">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={`rounded-full ${statusColors[v.status] || 'bg-sand-200'}`}>{v.status}</Badge>
                <span className="font-medium text-[#2C3D30]">{v.patient_name}</span>
                <Badge variant="outline" className="rounded-full">{v.discipline}</Badge>
                <span className="text-[#5C6A5F]">{v.staff_name}</span>
                <span className="text-sand-400">{v.scheduled_date}</span>
              </div>
              {v.status === 'scheduled' && (
                <Button onClick={() => completeVisit(v.id)} size="sm" variant="outline" className="rounded-full text-xs" data-testid={`visit-complete-${v.id}`}>
                  <CheckCircle className="w-3 h-3 mr-1" /> Complete
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// BILLING
// ═══════════════════════════════════════════════════════
function BillingPanel() {
  const [claims, setClaims] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState('');

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/workflow/billing/claims?status=all`).then(r => r.json()),
      fetch(`${API}/api/chart/patients-list`).then(r => r.json()),
    ]).then(([c, p]) => {
      setClaims(c.claims || []);
      setPatients(p.patients || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const generateClaim = async () => {
    if (!selectedPatient) return;
    setGenerating(true);
    try {
      const res = await fetch(`${API}/api/workflow/billing/generate-claim`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ patient_id: selectedPatient }),
      });
      const claim = await res.json();
      setClaims(prev => [claim, ...prev]);
    } catch {}
    setGenerating(false);
  };

  return (
    <div data-testid="billing-panel">
      <div className="flex items-center gap-2 mb-4">
        <DollarSign className="w-5 h-5 text-sage-400" />
        <h2 className="text-lg font-heading font-semibold text-[#2C3D30]">Billing & Claims</h2>
      </div>

      <div className="flex gap-2 mb-4">
        <select value={selectedPatient} onChange={e => setSelectedPatient(e.target.value)}
          className="flex-1 rounded-xl border border-sand-200 px-3 py-2 text-sm bg-sand-50" data-testid="billing-patient">
          <option value="">Select patient for claim...</option>
          {patients.map(p => <option key={p.id} value={p.id}>{p.name} — {p.level_of_care || 'Routine'}</option>)}
        </select>
        <Button onClick={generateClaim} disabled={generating || !selectedPatient} className="rounded-full bg-sage-400 hover:bg-sage-500 text-xs" data-testid="billing-generate-btn">
          {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <FileText className="w-3.5 h-3.5 mr-1" />} Generate Claim
        </Button>
      </div>

      {loading ? <Loader2 className="w-5 h-5 animate-spin text-sage-400 mx-auto" /> : (
        <div className="space-y-2" data-testid="billing-claims-list">
          {claims.length === 0 ? <p className="text-xs text-[#5C6A5F] text-center py-6">No claims generated yet.</p> :
            claims.map(c => (
              <div key={c.id} className="p-4 rounded-xl bg-sand-50 border border-sand-200 text-xs">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-[#2C3D30]">{c.patient_name}</span>
                  <Badge className="bg-sage-50 text-sage-400 rounded-full">{c.claim_type}</Badge>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div><span className="text-[#5C6A5F]">LOC:</span> <span className="font-medium text-[#2C3D30]">{c.level_of_care}</span></div>
                  <div><span className="text-[#5C6A5F]">Rate:</span> <span className="font-medium text-[#2C3D30]">${c.daily_rate}/day</span></div>
                  <div><span className="text-[#5C6A5F]">Days:</span> <span className="font-medium text-[#2C3D30]">{c.days_billed}</span></div>
                  <div><span className="text-[#5C6A5F]">Total:</span> <span className="font-medium text-sage-400">${c.total_amount?.toLocaleString()}</span></div>
                </div>
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// COMPLIANCE
// ═══════════════════════════════════════════════════════
function CompliancePanel() {
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState('');
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState('');

  useEffect(() => {
    fetch(`${API}/api/chart/patients-list`).then(r => r.json()).then(d => setPatients(d.patients || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedPatient) return;
    setLoading(true);
    fetch(`${API}/api/workflow/compliance/documents/${selectedPatient}`)
      .then(r => r.json()).then(d => setDocuments(d.documents || []))
      .catch(() => {}).finally(() => setLoading(false));
  }, [selectedPatient]);

  const generateF2F = async () => {
    if (!selectedPatient) return;
    setGenerating('f2f');
    try {
      const res = await fetch(`${API}/api/workflow/compliance/face-to-face`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ patient_id: selectedPatient, physician_name: "Dr. James Wilson" }),
      });
      const doc = await res.json();
      setDocuments(prev => [doc, ...prev]);
    } catch {}
    setGenerating('');
  };

  const generateEOB = async () => {
    if (!selectedPatient) return;
    setGenerating('eob');
    try {
      const res = await fetch(`${API}/api/workflow/compliance/election-of-benefits`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ patient_id: selectedPatient, effective_date: new Date().toISOString().split('T')[0], attending_physician: "Dr. James Wilson", hospice_physician: "Dr. Sarah Chen" }),
      });
      const doc = await res.json();
      setDocuments(prev => [doc, ...prev]);
    } catch {}
    setGenerating('');
  };

  return (
    <div data-testid="compliance-panel">
      <div className="flex items-center gap-2 mb-4">
        <Shield className="w-5 h-5 text-sky-400" />
        <h2 className="text-lg font-heading font-semibold text-[#2C3D30]">Compliance Documents</h2>
      </div>

      <select value={selectedPatient} onChange={e => setSelectedPatient(e.target.value)}
        className="w-full rounded-xl border border-sand-200 px-3 py-2 text-sm bg-sand-50 mb-3" data-testid="compliance-patient">
        <option value="">Select patient...</option>
        {patients.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>

      {selectedPatient && (
        <div className="flex gap-2 mb-4">
          <Button onClick={generateF2F} disabled={generating === 'f2f'} className="rounded-full bg-sky-300 hover:bg-sky-400 text-white text-xs" data-testid="compliance-f2f-btn">
            {generating === 'f2f' ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
            Face-to-Face Narrative
          </Button>
          <Button onClick={generateEOB} disabled={generating === 'eob'} className="rounded-full bg-sage-400 hover:bg-sage-500 text-white text-xs" data-testid="compliance-eob-btn">
            {generating === 'eob' ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <FileText className="w-3.5 h-3.5 mr-1" />}
            Election of Benefits
          </Button>
        </div>
      )}

      {loading ? <Loader2 className="w-5 h-5 animate-spin text-sky-300 mx-auto" /> : (
        <div className="space-y-3" data-testid="compliance-docs-list">
          {documents.map(doc => (
            <div key={doc.id} className="p-4 rounded-2xl bg-sand-50 border border-sand-200 border-l-4 border-l-sky-300" data-testid={`compliance-doc-${doc.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Badge className="bg-sky-50 text-sky-500 rounded-full text-xs">{doc.type === 'face_to_face' ? 'Face-to-Face' : 'Election of Benefits'}</Badge>
                  <span className="text-xs text-[#5C6A5F]">{doc.patient_name}</span>
                </div>
                <span className="text-xs text-sand-400">{new Date(doc.generated_at).toLocaleDateString()}</span>
              </div>
              <p className="text-sm text-[#2C3D30] leading-relaxed whitespace-pre-wrap">{doc.content}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════
export default function WorkflowBillingPage() {
  const [activeTab, setActiveTab] = useState('visits');

  const tabs = [
    { id: 'visits', label: 'Visit Schedule', icon: Calendar, color: 'sage' },
    { id: 'billing', label: 'Billing', icon: DollarSign, color: 'sage' },
    { id: 'compliance', label: 'Compliance', icon: Shield, color: 'sky' },
  ];

  const tabColors = {
    sage: 'bg-sage-50 text-sage-400 border border-sage-200',
    sky: 'bg-sky-50 text-sky-500 border border-sky-200',
  };

  return (
    <div className="min-h-screen bg-sand-100" data-testid="workflow-billing-page">
      <div className="max-w-5xl mx-auto p-4 sm:p-6 lg:p-8">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-[#2C3D30] tracking-tight">Workflow & Billing</h1>
          <p className="text-sm text-[#5C6A5F] mt-1">Visit scheduling, claims generation, and compliance documentation</p>
        </div>

        <div className="flex gap-2 mb-6 pb-3 border-b border-sand-200">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                activeTab === tab.id ? tabColors[tab.color] : 'text-[#5C6A5F] hover:bg-sand-200'
              }`} data-testid={`wf-tab-${tab.id}`}>
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        <div className="bg-sand-50 rounded-2xl border border-sand-200 p-4 sm:p-6">
          {activeTab === 'visits' && <VisitWorkflow />}
          {activeTab === 'billing' && <BillingPanel />}
          {activeTab === 'compliance' && <CompliancePanel />}
        </div>
      </div>
    </div>
  );
}
